import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LandingPage } from "@/pages/LandingPage";
import { DocsPage } from "@/pages/DocsPage";
import { LoginPage } from "@/pages/LoginPage";
import { SignupPage } from "@/pages/SignupPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { ProjectLayout } from "@/pages/ProjectLayout";
import { ProjectOverviewPage } from "@/pages/ProjectOverviewPage";
import { TablesPage } from "@/pages/TablesPage";
import { TableViewerPage } from "@/pages/TableViewerPage";
import { ViewViewerPage } from "@/pages/ViewViewerPage";
import { SQLEditorPage } from "@/pages/SQLEditorPage";
import { PermissionsPage } from "@/pages/PermissionsPage";
import { AdvisorsSecurityPage } from "@/pages/AdvisorsSecurityPage";
import { UsersPage } from "@/pages/UsersPage";
import { AuthLayout } from "@/pages/AuthLayout";
import { AuthSessionsPage } from "@/pages/AuthSessionsPage";
import { AuthSchemaPage } from "@/pages/AuthSchemaPage";
import { UsagePage } from "@/pages/UsagePage";
import { StoragePage } from "@/pages/StoragePage";
import { ImagesPage } from "@/pages/ImagesPage";
import { RealtimePage } from "@/pages/RealtimePage";
import { EdgeFunctionsPage } from "@/pages/EdgeFunctionsPage";
import { ObservabilityGatewayPage } from "@/pages/ObservabilityGatewayPage";
import { ObservabilityDatabasePage } from "@/pages/ObservabilityDatabasePage";
import { ObservabilityLogsPage } from "@/pages/ObservabilityLogsPage";
import { ObservabilityAuthPage } from "@/pages/ObservabilityAuthPage";
import { ObservabilityMCPPage } from "@/pages/ObservabilityMCPPage";
import { ObservabilityOverviewPage } from "@/pages/ObservabilityOverviewPage";
import { ObservabilityAlertsPage } from "@/pages/ObservabilityAlertsPage";
import { ObservabilityReportsPage } from "@/pages/ObservabilityReportsPage";
import { ApplicationsPage } from "@/pages/ApplicationsPage";
import { OperatorVerificationsPage } from "@/pages/OperatorVerificationsPage";
import { HostedAuthPortal } from "@/pages/HostedAuthPortal";
import { AccountCenterPage } from "@/pages/AccountCenterPage";
import { DatabaseManagementPage } from "@/pages/DatabaseManagementPage";
import { DatabaseLayout } from "@/pages/DatabaseLayout";
import { SchemaVisualizerPage } from "@/pages/SchemaVisualizerPage";
import { DatabaseFunctionsPage } from "@/pages/DatabaseFunctionsPage";
import { DatabaseTriggersPage } from "@/pages/DatabaseTriggersPage";
import { DASHBOARD_BASE, legacyToDashboardPath, ROUTES } from "@/routes";

/** Prefix legacy `/projects` and `/operator` bookmarks with `/dashboard`. */
function LegacyDashboardRedirect() {
  const location = useLocation();
  const next = legacyToDashboardPath(location.pathname);
  if (!next) {
    return <Navigate to={ROUTES.home} replace />;
  }
  return (
    <Navigate to={`${next}${location.search}${location.hash}`} replace />
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/docs/*" element={<DocsPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/account" element={<AccountCenterPage />} />
      <Route
        path="/auth/sign-in"
        element={<HostedAuthPortal action="signin" />}
      />
      <Route
        path="/auth/sign-up"
        element={<HostedAuthPortal action="signup" />}
      />
      <Route
        path="/auth/forgot-password"
        element={<HostedAuthPortal action="forgot-password" />}
      />
      <Route
        path="/auth/reset-password"
        element={<HostedAuthPortal action="reset-password" />}
      />
      <Route
        path="/auth/verify-email"
        element={<HostedAuthPortal action="verify-email" />}
      />
      <Route
        path="/auth/mcp/authorize"
        element={<HostedAuthPortal action="mcp-authorize" />}
      />
      <Route
        path="/auth/:projectSlug/sign-in"
        element={<HostedAuthPortal action="signin" />}
      />
      <Route
        path="/auth/:projectSlug/sign-up"
        element={<HostedAuthPortal action="signup" />}
      />
      <Route
        path="/auth/:projectSlug/forgot-password"
        element={<HostedAuthPortal action="forgot-password" />}
      />
      <Route
        path="/auth/:projectSlug/reset-password"
        element={<HostedAuthPortal action="reset-password" />}
      />
      <Route
        path="/auth/:projectSlug/verify-email"
        element={<HostedAuthPortal action="verify-email" />}
      />
      <Route
        path="/auth/:projectSlug/authorize"
        element={<HostedAuthPortal action="authorize" />}
      />
      <Route
        path="/auth/:projectSlug/account"
        element={<AccountCenterPage />}
      />

      {/* Platform console under /dashboard */}
      <Route path={DASHBOARD_BASE}>
        <Route index element={<Navigate to={ROUTES.projects} replace />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route
          path="operator/application-verifications"
          element={<OperatorVerificationsPage />}
        />
        <Route
          path="operator/application-verifications/:applicationId"
          element={<OperatorVerificationsPage />}
        />
        <Route path="projects/:id" element={<ProjectLayout />}>
          <Route index element={<ProjectOverviewPage />} />
          <Route path="tables" element={<TablesPage />} />
          <Route path="tables/:table" element={<TableViewerPage />} />
          <Route path="views/:view" element={<ViewViewerPage />} />
          <Route path="sql" element={<SQLEditorPage />} />
          <Route path="database" element={<DatabaseLayout />}>
            <Route index element={<DatabaseManagementPage />} />
            <Route path="schemas" element={<SchemaVisualizerPage />} />
            <Route path="tables" element={<TablesPage />} />
            <Route path="tables/:table" element={<TableViewerPage />} />
            <Route path="functions" element={<DatabaseFunctionsPage />} />
            <Route path="triggers" element={<DatabaseTriggersPage />} />
          </Route>
          <Route path="permissions" element={<PermissionsPage />} />
          <Route
            path="advisors"
            element={<Navigate to="security" replace />}
          />
          <Route path="advisors/security" element={<AdvisorsSecurityPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route
            path="applications/:applicationId"
            element={<ApplicationsPage />}
          />
          <Route path="users" element={<Navigate to="../auth/users" replace />} />
          <Route path="auth" element={<AuthLayout />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="sessions" element={<AuthSessionsPage />} />
            <Route path="schema" element={<AuthSchemaPage />} />
          </Route>
          <Route path="usage" element={<UsagePage />} />
          <Route path="storage" element={<StoragePage />} />
          <Route path="storage/images" element={<ImagesPage />} />
          <Route path="realtime" element={<RealtimePage />} />
          <Route path="functions" element={<EdgeFunctionsPage />} />
          <Route path="observability" element={<ObservabilityOverviewPage />} />
          <Route
            path="observability/gateway"
            element={<ObservabilityGatewayPage />}
          />
          <Route
            path="observability/database"
            element={<ObservabilityDatabasePage />}
          />
          <Route path="observability/logs" element={<ObservabilityLogsPage />} />
          <Route path="observability/auth" element={<ObservabilityAuthPage />} />
          <Route path="observability/mcp" element={<ObservabilityMCPPage />} />
          <Route
            path="observability/alerts"
            element={<ObservabilityAlertsPage />}
          />
          <Route
            path="observability/reports"
            element={<ObservabilityReportsPage />}
          />
        </Route>
      </Route>

      {/* Compatibility redirects for pre-prefix bookmarks */}
      <Route path="/projects/*" element={<LegacyDashboardRedirect />} />
      <Route path="/projects" element={<LegacyDashboardRedirect />} />
      <Route path="/operator/*" element={<LegacyDashboardRedirect />} />
      <Route path="/operator" element={<LegacyDashboardRedirect />} />

      <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
    </Routes>
  );
}
